/**
 * Voice-scope vocabulary (three layers):
 *
 * 1. Outcome kind — routing for the UI after KaiAgent parses an utterance:
 *    `propose` | `reject` | `ignore`
 *    Two JEV channels run concurrently per utterance when scoped work exists:
 *    full (add + update) and scoped-update (update on in-scope work only).
 * 2. Operation — JEV `answers.operation.choice`: what the user meant (`add` | `update` | none)
 * 3. Command — scan mutation payload: `VoiceScopeCommand.type` (`addOption` | `setTaskValue`)
 *
 * Activity-log lifecycle (separate from outcome kind):
 * - `voice-proposed` — pending Apply/Dismiss row
 * - `voice-applied` — user confirmed; scan was updated via `applyCommands`
 */
export type VoiceScopeSnapshotValue = {
  name: string;
  type: string;
  value?: number;
  description?: string;
  optionId?: string;
  lineItemId?: string;
};

export type VoiceScopeSnapshotLineItem = {
  id: string;
  templateId: string;
  name: string;
};

export type VoiceScopeSnapshotOption = {
  id: string;
  templateId: string;
  name: string;
  lineItems: VoiceScopeSnapshotLineItem[];
};

export type VoiceScopeSnapshotTask = {
  id: string;
  templateId: string;
  name: string;
  options: VoiceScopeSnapshotOption[];
  values: VoiceScopeSnapshotValue[];
  photoCount: number;
  totalCost?: number;
};

export type VoiceScopeCatalogLineItem = {
  templateId: string;
  name: string;
  description?: string;
  isProduct?: boolean;
};

export type VoiceScopeCatalogOption = {
  templateId: string;
  name: string;
  description?: string;
  lineItems: VoiceScopeCatalogLineItem[];
};

export type VoiceScopeCatalogTask = {
  templateId: string;
  name: string;
  description?: string;
  existingTaskId?: string;
  options: VoiceScopeCatalogOption[];
  values: VoiceScopeSnapshotValue[];
};

export type VoiceScopeSnapshot = {
  version: number;
  group: { id: string; name: string; tasks: VoiceScopeSnapshotTask[] };
  catalog: VoiceScopeCatalogTask[];
};

type VoiceScopeCommandBase = {
  commandId: string;
  groupId: string;
  taskId: string;
  utterance: string;
};

export type VoiceScopeAddOptionCommand = VoiceScopeCommandBase & {
  type: 'addOption';
  taskTemplateId: string;
  taskName: string;
  optionTemplateId: string;
  optionName: string;
  lineItemTemplateId?: string;
  lineItemName?: string;
  quantity?: number;
  spokenPrice?: number;
};

export type VoiceScopeSetTaskValueCommand = VoiceScopeCommandBase & {
  type: 'setTaskValue';
  taskName: string;
  valueName: string;
  /** What the user sees for the value (its description, else its name). */
  valueLabel: string;
  valueType: string;
  value: number;
  optionId?: string;
  lineItemId?: string;
};

export type VoiceScopeCommand = VoiceScopeAddOptionCommand | VoiceScopeSetTaskValueCommand;

export type JevQuestion = {
  type: string;
  instructions: string;
  criteria?: Record<string, string>;
};

export type JevAnswer = {
  type: string;
  noul?: number;
  choice?: string;
  confidence?: number;
  probabilities?: Record<string, number>;
};

export type JevEvaluateResponse = {
  model: string;
  answers: Record<string, JevAnswer>;
  usage?: { input_tokens?: number; output_tokens?: number };
};

export type VoiceScopeOrchestratorConfig = {
  confidenceThreshold: number;
  compoundPickThreshold: number;
  transcriptWindow: number;
  recentEditsWindow: number;
};

export const DEFAULT_VOICE_SCOPE_ORCHESTRATOR_CONFIG: VoiceScopeOrchestratorConfig = {
  confidenceThreshold: 0.6,
  compoundPickThreshold: 0.3,
  transcriptWindow: 8,
  recentEditsWindow: 5,
};

export type VoiceScopeProposeOutcome = {
  kind: 'propose';
  utterance: string;
  commands: VoiceScopeCommand[];
  summary: string;
  confidence: number;
  operationGatePassed: boolean;
  usedBestGuess: boolean;
  jevDetail: string;
  scanPartId: string;
};

export type VoiceScopeRejectOutcome = {
  kind: 'reject';
  utterance: string;
  reason: string;
  /** Set when reject is due to JEV confidence below the orchestrator threshold. */
  confidence?: number;
};

export type VoiceScopeIgnoreOutcome = {
  kind: 'ignore';
  utterance: string;
};

export type VoiceScopeOrchestratorOutcome =
  | VoiceScopeProposeOutcome
  | VoiceScopeRejectOutcome
  | VoiceScopeIgnoreOutcome;
